import {clsx} from 'clsx';
import {Button, Typography} from 'onboarding-components';
import * as React from 'react';

import {SvgIconReact} from 'common/components/svg-icon';

import styles from '../index.module.styl';
import {track, TrackingEvent, TrackingProperty} from '../tracking';
import {SetupGuideContext, Step} from '../types';
import {ScrollableLink} from './scroll-manager';

const StepIndicatorV1 = ({isCompleted, moniker, children, step: targetStep}) => {
  const {step} = React.useContext(SetupGuideContext);

  return (
    <ScrollableLink
      className={clsx({[styles.completed]: isCompleted}, styles.indicator)}
      targetStep={targetStep}
      onClick={() => {
        track(TrackingEvent.StepClick, {[TrackingProperty.Source]: `SideNav`, [TrackingProperty.StepName]: targetStep});
      }}
    >
      {isCompleted ? (
        <div className={clsx(styles.moniker, styles.completed, {[styles.current]: step === targetStep})}>
          <SvgIconReact icon='checkmark' />
        </div>
      ) : (
        <div className={clsx(styles.moniker, {[styles.current]: step === targetStep})}>
          <Typography variant='eyebrowSm' weight='bold' style={{letterSpacing: 0, lineHeight: `100%`}}>
            {moniker}
          </Typography>
        </div>
      )}
      <Typography variant='bodySm' weight='bold'>
        {children}
      </Typography>
    </ScrollableLink>
  );
};

const StepIndicatorV2 = ({isCompleted, moniker, children, step: targetStep}) => {
  const {step, setStep} = React.useContext(SetupGuideContext);

  return (
    <div
      className={clsx({[styles.completed]: isCompleted}, styles.indicator, {[styles.current]: step === targetStep})}
      onClick={() => {
        setStep(targetStep);
        track(TrackingEvent.StepClick, {[TrackingProperty.Source]: `SideNav`, [TrackingProperty.StepName]: targetStep});
      }}
    >
      {isCompleted ? (
        <div className={clsx(styles.moniker, styles.completed, {[styles.current]: step === targetStep})}>
          <SvgIconReact icon='checkmark' />
        </div>
      ) : (
        <div className={clsx(styles.moniker, {[styles.current]: step === targetStep})}>
          <Typography variant='eyebrowSm' weight='bold' style={{letterSpacing: 0, lineHeight: `100%`}}>
            {moniker}
          </Typography>
        </div>
      )}
      <Typography variant='bodySm' weight='bold'>
        {children}
      </Typography>
    </div>
  );
};

export const SideNavPhase2 = ({onClose}) => {
  const {completed} = React.useContext(SetupGuideContext);

  return (
    <div className={styles.nav}>
      <div>
        <Typography variant='headingMd' style={{margin: 0}}>{`Setup Guide`}</Typography>
        <Typography variant='bodySm' className={styles.subtitle}>{`Up and running in 20 minutes`}</Typography>
        <div className={styles.indicatorGroup}>
          {/* Replace with components */}
          <StepIndicatorV2 isCompleted={completed[Step.Welcome]} moniker={1} step={Step.Welcome}>
            Start Here
          </StepIndicatorV2>
          <StepIndicatorV2 isCompleted={completed[Step.Events]} moniker={2} step={Step.Events}>
            Personalize Setup
          </StepIndicatorV2>
          <StepIndicatorV2 isCompleted={completed[Step.Implement]} moniker={3} step={Step.Implement}>
            Connect Data
          </StepIndicatorV2>
        </div>
      </div>
      <Button onClick={onClose} variant='inline' color='dark' className={styles.footerButton}>
        {completed[Step.Welcome] && completed[Step.Events] && completed[Step.Implement] ? `Close` : `Skip for Now`}
      </Button>
    </div>
  );
};

export const SideNav = ({onClose}) => {
  const {completed} = React.useContext(SetupGuideContext);

  return (
    <div className={styles.nav}>
      <div>
        <Typography variant='headingMd' style={{margin: 0}}>{`Setup Guide`}</Typography>
        <Typography variant='bodySm' className={styles.subtitle}>{`Up and running in 20 minutes`}</Typography>
        <div className={styles.indicatorGroup}>
          {/* Replace with components */}
          <StepIndicatorV1 isCompleted={completed[Step.Events]} moniker={1} step={Step.Events}>
            Personalize your setup
          </StepIndicatorV1>
          <StepIndicatorV1 isCompleted={completed[Step.Implement]} moniker={2} step={Step.Implement}>
            Connect your data
          </StepIndicatorV1>
          <StepIndicatorV1 isCompleted={false} moniker={3} step={Step.Explore}>
            Explore product insights
          </StepIndicatorV1>
        </div>
      </div>
      <Button onClick={onClose} variant='inline' color='dark' className={styles.footerButton}>
        Skip and continue later
      </Button>
    </div>
  );
};
